import React, {useCallback} from "react"
import {Tooltip} from "antd"
import {observer} from "mobx-react"
import {useHistory} from "react-router-dom"
import {isEmpty} from "lodash"
import {useMsal} from "@azure/msal-react"
import cx from "classnames"
import {useTranslation} from "react-i18next"
import {Icon, IconType} from "components/Icon"
import {useCurrentProfilePermissions, useModel} from "hooks"
import {Permissions} from "types/permission"
import {useEdularModulesContext, Modules} from "@edular/modules"
import styles from "./InfoBar.module.css"
import {getUnreadCountBadge, store} from "helpers"
import {Auth} from "types/auth"
import {UserIcon} from "./parts"
import {offEvent, onEvent} from "helpers/eventHelper"
import {EVENT_API_REQUEST_UNAUTHORIZED} from "services/Base"

type Item = {
    title: string
    icon: IconType
    attentionCount?: number
    to?: string
    action?: string
}

type Props = {
    photo: any
}

export const InfoBar = observer((props: Props) => {
    const locale = store.get("locale") || Auth.DisplayLanguage.English

    const model = useModel()
    const {isModuleEnable} = useEdularModulesContext()
    const history = useHistory()
    const {instance, accounts} = useMsal()
    const currentProfilePermissions = useCurrentProfilePermissions()
    const {t, i18n} = useTranslation(["sidebar"])
    const {user} = model
    let {photo} = user

    if (photo) {
        photo = photo[64]
    }

    const loadLocale = useCallback(() => {
        i18n.changeLanguage(locale)
    }, [i18n, locale])

    React.useEffect(() => {
        loadLocale()
    }, [loadLocale])

    const userData = {
        userId: user?.id,
        firstName: user?.firstName,
        lastName: user?.lastName,
        image: photo
    }

    const getKeysByRole = (): Item[] => {
        const canShowTasks =
            isEmpty(model.departments) || !model.isStaff()
                ? true
                : model.departments.some((department) => {
                      return currentProfilePermissions.departmentTasks.hasPermissionsToView(department.id)
                  })
        return [
            {
                title: t("menu.concerns"),
                icon: "QUESTION_OPEN",
                to: "/concerns"
            },
            isModuleEnable(Modules.ActivitiesAndFollowups) && {
                title: t("menu.activities"),
                icon: "CHECKMARK_CIRCLE",
                to: "/activities"
            },
            {
                title: t("menu.Notification"),
                icon: "BELL",
                to: "/notifications",
                attentionCount: model?.chatOptions?.unreadNotification || 0
            },
            isModuleEnable(Modules.Chat) && {
                title: t("menu.Messages"),
                icon: "MESSAGE_CIRCLE",
                to: "/communication",
                attentionCount: model?.chatOptions?.unreadCount || 0
            },
            isModuleEnable(Modules.Tasks) &&
                canShowTasks && {
                    title: t("menu.Tasks"),
                    icon: "TASK_FILL",
                    to: "/tasks"
                },
            currentProfilePermissions.hasPermissions({
                staff: [Permissions.Staff.Resources.MediaLibrary.General.View],
                student: [Permissions.Student.Resources.MediaLibrary.View],
                others: [Permissions.Others.Resources.MediaLibrary.View]
            }) && {
                title: t("menu.Favorite"),
                icon: "STAR_FILL",
                to: "/favorite"
            },
            // {
            //     title: "Knowledge base",
            //     icon: "KNOWLEDGE",
            //     action: "knowledge"
            // },
            isModuleEnable(Modules.Calendar) && {
                title: t("menu.Calendar"),
                icon: "CALENDAR_FILL_INPUT",
                to: "/calendar"
            },
            model.clientSetting.isSupportChatEnabled && {
                title: t("menu.supportHub"),
                icon: "SUPPORT_HUB",
                to: "/supports"
            },
            {
                title: t("menu.Logout"),
                icon: "LOGOUT",
                action: "logout"
            }
        ].filter((item: any) => item) as Item[]
    }

    const logout = React.useCallback(async () => {
        await model.logout()
        const [currentAccount] = accounts
        if (currentAccount) {
            await instance.logoutRedirect({account: currentAccount, postLogoutRedirectUri: window.location.origin})
        } else {
            setTimeout(() => {
                window.location.href = "/"
            }, 500)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [instance, accounts])

    const handleClick = (item: Item) => {
        if (item.action === "logout") {
            logout()
        } else if (item.action === "knowledge") {
            window.open("https://docs.edular.com ", "_blank")
        } else {
            history.push(item.to)
        }
    }

    React.useEffect(() => {
        function handleApiRequestUnauthorized() {
            logout()
        }

        onEvent(EVENT_API_REQUEST_UNAUTHORIZED, handleApiRequestUnauthorized)
        return () => {
            offEvent(EVENT_API_REQUEST_UNAUTHORIZED, handleApiRequestUnauthorized)
        }
    }, [logout])

    return (
        <div className={styles.wrapper}>
            <ul className={styles.list}>
                {getKeysByRole().map((item: Item) => {
                    return <Li item={item} key={item.title} onClick={handleClick} />
                })}
            </ul>
            <div className={styles.divider}></div>
            <button className={cx(styles.button, styles.buttonTransparent)}>
                <UserIcon {...userData} />
            </button>
        </div>
    )
})

function onKeyDown(event) {
    const element: HTMLAnchorElement = event.currentTarget
    if (event.keyCode === 13 || event.keyCode === 32) {
        removeFocus(element)
    }
}

function removeFocus(element: HTMLElement) {
    if (element) {
        element.focus()
        element.blur()
    }
}

const Li = observer(({item, onClick}: {item: Item; onClick: (item: Item) => void}) => {
    return (
        <li className={styles.item} onClick={(e) => onClick(item)}>
            <Tooltip placement="right" title={item.title}>
                <button
                    className={styles.button}
                    onMouseDown={(e) => {
                        e.preventDefault()
                        const element: HTMLAnchorElement = e.currentTarget as unknown as HTMLAnchorElement
                        removeFocus(element)
                    }}
                    onKeyUp={(e) => {
                        onKeyDown(e)
                    }}>
                    {item.attentionCount > 0 && (
                        <div className={styles.buttonAttention}>{getUnreadCountBadge(item.attentionCount)}</div>
                    )}
                    <Icon className={styles.iconItem} icon={item.icon} color="var(--primary-400-base)" />
                </button>
            </Tooltip>
        </li>
    )
})
